import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View, TouchableOpacity } from 'react-native';
import { ComponentWrapper } from '../../helperComponents/ComponentWrapper';
import { Divider } from '../../helperComponents/Divider';
import { Input } from '../../helperComponents/Input';
import { applyTransparency } from '../../../constants/colors';
import { MenuButton } from '../../helperComponents/MenuButton';
import { BackButton } from '../../helperComponents/BackButton';
import { ScreenProps } from '../../../typesInterfacesEnums/componentProps';
import { mbPlatformStyle } from '@mightybyte/rnw.utils.style-utils';
import { useSignUpMutation } from '../SignUp/useSingUpMutation.gql';
import { USER_TYPES } from '../../../typesInterfacesEnums/enums';
import { MB_accessTokenUtils } from '@mightybyte/rnw.utils.access-token-utils';
import { SIGNED_IN_STATUS, useSignedInContext } from '../../../context/SignedInContext';
import { ErrorPopup } from '../../helperComponents/ErrorPopup';
import { ApolloError } from '@apollo/client';
import { useRequestSignUpCodeMutation } from '../SignUp/useRequestSignUpCodeMutation.gql';
import { getErrorText } from '../../../utils/errors';

const VerifyEmail = ({ navigation, route }: ScreenProps<'VerifyEmail'>) => {
  const { devCode } = route.params;
  const { setSignedInStatus } = useSignedInContext();
  const [code, setCode] = useState(devCode ?? '');
  const [signUp, { data, loading }] = useSignUpMutation({
    variables: { firstName: route.params.firstName, lastName: route.params.lastName, password: route.params.password, emailOrUsername: route.params.email, userType: USER_TYPES.teacher, signUpCode: code },
  });
  const [requestCode, { loading: requestCodeLoading }] = useRequestSignUpCodeMutation({
    variables: { email: route.params.email, name: route.params.firstName + ' ' + route.params.lastName },
  });
  const [hasError, setHasError] = useState({
    visible: false,
    popup: false,
    text: '',
  });
  const handleSignUpError = (err: ApolloError) => {
    setHasError({ popup: true, visible: true, text: getErrorText(err) });
  };

  const handleVerifyInstantly = () => {
    signUp({ variables: {
      firstName: route.params.firstName,
      lastName: route.params.lastName,
      password: route.params.password,
      emailOrUsername: route.params.email,
      userType: USER_TYPES.teacher,
      signUpCode: devCode!,
    }}).catch(handleSignUpError);
  };

  useEffect(() => {
    if (data) {
      const setAccessToken = async () => {
        let accessToken = { ...data.signup.accessToken };
        delete accessToken.__typename;
        await MB_accessTokenUtils.setAccessToken({ accessToken: accessToken, refreshToken: data.signup.refreshToken });
        setSignedInStatus(SIGNED_IN_STATUS.signedIn);
        navigation.navigate('Lobby');
      };
      setAccessToken();
    }
  }, [data, navigation, setSignedInStatus]);


  return (
    <>
      <ComponentWrapper
        hasInset
        hasScroll={false}
        scrollEnabled={false}
        style={{ paddingHorizontal: 0 }}
      >
        <View style={styles.topContainer}>
          <BackButton onPress={() => navigation.goBack()} text="Back" />
        </View>
        <Text maxFontSizeMultiplier={1} style={styles.title}>
          Verify your email
        </Text>
        <Divider empty height={12} />
        <Text maxFontSizeMultiplier={1} style={styles.subtitle}>
          Please enter the verification code that was sent to your email address.
        </Text>
        <Divider empty height={40} />
        {devCode ? (
          <>
            <View style={styles.devBanner}>
              <Text style={styles.devBannerTitle}>Dev mode — not shown in production</Text>
              <Text style={styles.devBannerBody}>
                Your verification code is:{'\n'}
                <Text style={styles.devBannerCode}>{devCode}</Text>
              </Text>
              <Text style={styles.devBannerNote}>
                This panel is only visible on self-hosted and development instances to make test account creation easier.
              </Text>
            </View>
            <Divider empty height={20} />
            <MenuButton
              onPress={handleVerifyInstantly}
              colors={['#AAAAAA', '#888888']}
              text="Verify instantly (dev only)"
              textStyle={{ fontSize: 14 }}
              height={50}
              width={300}
              loading={loading}
            />
            <Divider empty height={20} />
          </>
        ) : null}
        <Input
          value={code}
          setValue={(value) => setCode(value)}
          style={{ width: '100%', maxWidth: 320 }}
          textStyle={{ fontSize: 18 }}
          placeholder="Please enter the code"
          onSubmitEditing={() => signUp().catch((error) => handleSignUpError(error))}
        />
        <Divider empty height={60} />
        <MenuButton
          onPress={() => signUp().catch((error) => handleSignUpError(error))}
          colors={['#90DF75', '#62B655']}
          text="Verify email"
          height={64}
          width={320}
          loading={loading || requestCodeLoading}
          disabled={loading || requestCodeLoading || code.length !== 6}
        />
        <Divider empty height={20} />
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text maxFontSizeMultiplier={1} style={styles.resendText}>
            Didn't receive code?
          </Text>
          <TouchableOpacity onPress={() => requestCode().catch((error) => handleSignUpError(error))}>
            <Text style={styles.resendText2}>
              Resend
            </Text>
          </TouchableOpacity>
        </View>
      </ComponentWrapper>
      <ErrorPopup
        isVisible={hasError.popup}
        setVisible={() => setHasError(v => ({ ...v, popup: false }))}
        text={hasError.text}
      />
    </>
  );
};

export { VerifyEmail };

const styles = StyleSheet.create({
  image: { width: 180, height: 100 },
  howToPlayText: {
    fontFamily: 'Secular One',
    fontSize: 20,
    color: 'black',
    textDecorationLine: 'underline',
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 32,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#121212',
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: 'Secular One',
    fontSize: 18,
    width: 320,
    color: '#E89823',
    textAlign: 'center',
  },
  topContainer: {
    height: 60,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    position: 'absolute',
    top: 60,
    ...mbPlatformStyle({
      mobile: {
        paddingHorizontal: 24,
      },
      web: {
        paddingHorizontal: 120,
      },
    }),
  },
  devBanner: {
    width: '100%',
    maxWidth: 320,
    backgroundColor: '#FFF8E7',
    borderWidth: 1,
    borderColor: '#E89823',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
  },
  devBannerTitle: {
    fontFamily: 'Secular One',
    fontSize: 13,
    color: '#E89823',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  devBannerBody: {
    fontFamily: 'Secular One',
    fontSize: 15,
    color: '#444444',
    textAlign: 'center',
  },
  devBannerCode: {
    fontFamily: 'Secular One',
    fontSize: 24,
    color: '#121212',
    letterSpacing: 4,
  },
  devBannerNote: {
    fontFamily: 'Secular One',
    fontSize: 12,
    color: '#888888',
    textAlign: 'center',
    marginTop: 8,
  },
  beeIcon: {
    width: 25,
    height: 38,
  },
  forgetContainer: {
    width: 320,
    justifyContent: 'flex-start',
  },
  forgetText: {
    fontFamily: 'Secular One',
    fontSize: 16,
    color: '#E89823',
    textAlign: 'right',
  },
  signUpContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  signUpText: {
    fontFamily: 'Secular One',
    fontSize: 20,
    color: '#453B3B',
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 2,
  },
  resendText: {
    fontFamily: 'Secular One',
    fontSize: 20,
    color: '#453B3B',
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 2,
  },
  resendText2: {
    color: '#E89823',
    fontFamily: 'Secular One',
    fontSize: 20,
    marginLeft: 6,
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    textDecorationColor: '#E89823',
  },
});
